import React, { useState } from 'react';
import { formatDateTime } from '../../helpers/formatDateTime';
import type { BureauAlarm } from '../../services/bureauManagement/bureauManagement.types';
import { StatusMessage } from '../ui';
import Modal from '../ui/Modal';
import { ModalContent } from '../ui/ModalContent';

interface Props {
    selectedAlarms: BureauAlarm[];
    onAcknowledge: (alarms: BureauAlarm[]) => Promise<void>;
    actionInFlight?: string | null;
    onRemoveSelected?: (id: number) => void;
    onClose?: () => void;
    isInsideModal?: boolean;
}

const BureauAcknowledgeForm: React.FC<Props> = ({ 
    selectedAlarms, 
    onAcknowledge, 
    actionInFlight, 
    onRemoveSelected,
    onClose,
    isInsideModal = false
}) => {
    const [error, setError] = useState<string | null>(null);

    const [openConfirm, setOpenConfirm] = useState<boolean>(false);

    // Helper to replace "Bureau" with "Connected Services Hub"
    const formatRecipientName = (value: string | undefined | null): string => {
        if (!value || value === 'Unknown') {
            return value || 'Unknown';
        }
        // If the value is just "Bureau" repeated replace with "Connected Services Hub"
        if (/^(Bureau\s*)+$/i.test(value.trim())) {
            return 'Connected Services Hub';
        }
        return value;
    };

    if (!selectedAlarms || selectedAlarms.length === 0) {
        return null;
    }

    const handleAcknowledgeAll = async () => {
        if (isInsideModal) {
            // When inside a modal, directly call onAcknowledge (parent modal's OK will trigger this)
            setError(null);
            try {
                await onAcknowledge(selectedAlarms);
            } catch (err) {
                setError((err as Error).message || 'Failed to acknowledge alarms');
            }
        } else {
            // When standalone, open confirmation modal
            setOpenConfirm(true);
        }
    };

    const confirmAcknowledgeAll = async () => {
        setOpenConfirm(false);
        setError(null);
        try {
            await onAcknowledge(selectedAlarms);
        } catch (err) {
            setError((err as Error).message || 'Failed to acknowledge alarms');
        }
    };

    const processing = Boolean(actionInFlight);

    return (
        <div id="bureau-acknowledge-form">
            {!isInsideModal && (
                <>
                <Modal
                    isOpen={openConfirm}
                    title=""
                    content={<ModalContent
                        heading={`Acknowledge ${selectedAlarms.length} alarm(s)`}
                        message={`Are you sure you want to acknowledge the ${selectedAlarms.length} selected alarm(s)?`}
                    />}
                    onConfirm={confirmAcknowledgeAll}
                    onCancel={() => setOpenConfirm(false)}
                />
                <h3 className='heading heading__title--alarm'>
                    Acknowledge CSH Alarms ({selectedAlarms.length})
                </h3>
                </>
            )}

            {isInsideModal && (
                <h3 className="heading heading__title--alarm">Acknowledge CSH Alarms ({selectedAlarms.length})</h3>
            )}
            
            {error && <StatusMessage kind="error">{error}</StatusMessage>}

            <div className="">
                {selectedAlarms.map(alarm => {
                    const alarmId = alarm.bureauAlarmId || alarm.id || 0;
                    return (
                        <div key={alarmId} className="form-entry mb-2" style={{ borderBottom: '1px solid #eee' }}>
                            <div className="form-entry__heading">
                                <h3 className='heading__title--building'>
                                    {alarm.siteName || alarm.siteIdentifier || `Alarm ${alarmId}`}
                                </h3>
                                <button
                                    type="button"
                                    className="btn__close se-icon--close"
                                    onClick={() => onRemoveSelected?.(alarmId)}
                                    disabled={processing}
                                />
                            </div>
                            <div className="form-row">
                                <label>Site</label>
                                <div className="form-row__info">{alarm.siteName || alarm.siteIdentifier || 'Unknown'}</div>
                            </div>
                            <div className="form-row">
                                <label>Recipient</label>
                                <div className="form-row__info">{formatRecipientName(alarm.bureauRecipientName || alarm.bureauName)}</div>
                            </div>
                            <div className="form-row">
                                <label>Path</label>
                                <div className="form-row__info">{alarm.pathToAlarm || alarm.path || alarm.alarmIdentifier || '-'}</div>
                            </div>
                            <div className="form-row">
                                <label>State</label>
                                <div className="form-row__info">{alarm.bureauAlarmState || 'Unknown'}</div>
                            </div>
                            <div className="form-row">
                                <label>Last Occurrence</label>
                                <div className="form-row__info">{formatDateTime(alarm.lastOccurrenceAt)}</div>
                            </div>
                            <div className="form-row">
                                <label>Status</label>
                                <div className="form-row__info">{alarm.localAck ? 'Already Acknowledged' : 'Not Acknowledged'}</div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {!isInsideModal && (
                <div className="form-actions">
                    <button 
                        type="button" 
                        className="btn btn--success mb-2" 
                        onClick={handleAcknowledgeAll} 
                        disabled={processing}
                    >
                        {processing ? 'Processing…' : `Acknowledge ${selectedAlarms.length} alarm(s)`}
                    </button>
                    <button 
                        type="button" 
                        className="btn btn--secondary mb-2" 
                        onClick={onClose} 
                        disabled={processing}
                    >
                        Cancel
                    </button>
                </div>
            )}
        </div>
    );
};

export default BureauAcknowledgeForm;
